import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cone, Circle, Goal, Shirt, Package, FolderOpen, FileUp, Trash2, ThumbsUp, ThumbsDown, MessageCircle, Reply } from 'lucide-react';
import {
  TrainingSession,
  TrainingSessionResponse,
  TimeBlock,
  DrillGroup,
  DrillImageMetadata,
  Dimension,
  SessionNoteResponse,
  CommentResponse,
  CommentReplyResponse,
  ReactionSummaryResponse,
  isImageDrill,
} from '../Model';
import { formatTimeRange } from '../utils';
import { restClient } from '../RestClient';
import { YouTubeEmbed, getYouTubeVideoId } from '../YouTubeEmbed';
import { DrillImageGallery } from '../DrillImageGallery';
import { SessionFilters } from './SessionFilters';
import { useReferenceData } from '../ReferenceDataContext';

interface TrainingSessionsProps {
  onError?: (message: string) => void;
  isAdmin?: boolean;
}

// Session List Item Component
interface SessionListItemProps {
  session: TrainingSession;
  isSelected: boolean;
  onClick: () => void;
}

const SessionListItem: React.FC<SessionListItemProps> = ({ session, isSelected, onClick }) => {
  const formattedDate = new Date(session.createdAt).toLocaleDateString();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={`session-list-item ${isSelected ? 'session-list-item-selected' : ''}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
      aria-label={`Select ${session.name} session`}
    >
      <div className="session-list-item-header">
        <span className="session-list-item-name">{session.name}</span>
        <span className="badge badge-sm badge-accent">{session.sessionFocus}</span>
      </div>
      <div className="session-list-item-meta">
        <span>{session.totalTime} min</span>
        <span className="text-muted">|</span>
        <span>{session.players} players</span>
        <span className="text-muted">|</span>
        <span>{session.coaches} coach{session.coaches !== 1 ? 'es' : ''}</span>
      </div>
      <div className="session-list-item-date text-muted text-sm">
        {formattedDate}
      </div>
    </div>
  );
};

// Session List Component
interface SessionListProps {
  responses: TrainingSessionResponse[];
  selectedResponse: TrainingSessionResponse | null;
  onSelect: (response: TrainingSessionResponse) => void;
}

const SessionList: React.FC<SessionListProps> = ({ responses, selectedResponse, onSelect }) => {
  return (
    <>
      <h3 className="section-title mb-4">Saved Sessions ({responses.length})</h3>
      <div className="session-list">
        {responses.map(response => (
          <SessionListItem
            key={response.session.id}
            session={response.session}
            isSelected={selectedResponse?.session.id === response.session.id}
            onClick={() => onSelect(response)}
          />
        ))}
      </div>
    </>
  );
};

// Pitch Layout Diagram Component
interface PitchLayoutDiagramProps {
  pitchDimensions: Dimension;
  drillGroups: DrillGroup[];
}

const PitchLayoutDiagram: React.FC<PitchLayoutDiagramProps> = ({ pitchDimensions, drillGroups }) => {
  const viewWidth = 300;
  const viewHeight = (pitchDimensions.height / pitchDimensions.width) * viewWidth;
  const scaleX = viewWidth / pitchDimensions.width;
  const scaleY = viewHeight / pitchDimensions.height;

  const colors = ['#667eea', '#28a745', '#ffc107', '#dc3545', '#17a2b8'];

  const allStations = drillGroups.flatMap(g =>
    g.stations.map(s => ({ ...s, drillName: g.drill.name }))
  );

  return (
    <div className="card mb-4">
      <div className="card-body">
        <h4 className="pitch-layout-title">Pitch Layout</h4>
        <svg
          viewBox={`0 0 ${viewWidth} ${viewHeight}`}
          role="img"
          aria-label={`Pitch layout showing ${allStations.length} group${allStations.length !== 1 ? 's' : ''}`}
          className="pitch-svg"
        >
          {/* Center circle */}
          <circle
            cx={viewWidth / 2}
            cy={viewHeight / 2}
            r={Math.min(viewWidth, viewHeight) * 0.15}
            fill="none"
            stroke="#28a745aa"
            strokeWidth={1.5}
          />
          {/* Center line */}
          <line
            x1={viewWidth / 2}
            y1={0}
            x2={viewWidth / 2}
            y2={viewHeight}
            stroke="#28a745aa"
            strokeWidth={1.5}
          />

          {/* Station areas */}
          {allStations.map((station, idx) => {
            const area = station.pitchArea;
            return (
              <g key={`${station.id}-${idx}`}>
                <rect
                  x={area.x * scaleX}
                  y={area.y * scaleY}
                  width={area.width * scaleX}
                  height={area.height * scaleY}
                  fill={`${colors[idx % colors.length]}40`}
                  stroke={colors[idx % colors.length]}
                  strokeWidth={2}
                  rx={4}
                />
                <text
                  x={(area.x + area.width / 2) * scaleX}
                  y={(area.y + area.height / 2) * scaleY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill={colors[idx % colors.length]}
                >
                  {station.assignedPlayers.groupName}
                </text>
              </g>
            );
          })}
        </svg>
        <div className="pitch-legend">
          {allStations.map((station, idx) => (
            <div key={`${station.id}-${idx}`} className="pitch-legend-item">
              <div className="pitch-legend-color" style={{ backgroundColor: colors[idx % colors.length] }} />
              <span className="pitch-legend-label">{station.drillName} - {station.assignedPlayers.groupName}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Drill Card Component (Read-Only) - Shows drill info once with station assignments
interface DrillCardProps {
  group: DrillGroup;
  showCoach: boolean;
}

const DrillCard: React.FC<DrillCardProps> = ({ group, showCoach }) => {
  const [showVideo, setShowVideo] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const [drillImages, setDrillImages] = useState<DrillImageMetadata[]>([]);
  const drill = group.drill;
  const videoId = drill.youtubeUrl ? getYouTubeVideoId(drill.youtubeUrl) : null;
  const hasImages = isImageDrill(drill);

  const handleShowImages = async () => {
    if (!showImages && drillImages.length === 0) {
      const result = await restClient.getDrillImageMetadata(drill.id);
      if (result.IsSuccess) setDrillImages(result.Value);
    }
    setShowImages(!showImages);
  };

  return (
    <div className="station-card">
      <div className="station-header">
        <div className="station-name">{drill.name}</div>
      </div>

      {/* Station assignments */}
      <div className="station-groups">
        {group.stations.map((station) => (
          <div key={station.id} className="station-group-info">
            <span>{station.assignedPlayers.groupName} ({station.assignedPlayers.count} players)</span>
            {showCoach && station.assignedCoaches.length > 0 && (
              <span className="station-coach-badge">
                {station.assignedCoaches.length === 1
                  ? `Coach ${station.assignedCoaches[0]}`
                  : `Coaches ${station.assignedCoaches.join(', ')}`}
              </span>
            )}
            <span className="text-sm text-muted">
              {station.pitchArea.label}
            </span>
          </div>
        ))}
      </div>

      {/* Description */}
      <p className="text-sm text-muted mt-2 mb-2 line-height-relaxed">
        {drill.description}
      </p>

      {/* Skills */}
      <div className="station-skills">
        {drill.skills.map((skill) => (
          <span key={skill} className="station-skill-badge">
            {skill}
          </span>
        ))}
      </div>

      {/* Equipment */}
      {drill.equipment && (
        <div className="mt-2 text-sm text-muted">
          <span className="font-semibold">Equipment: </span>
          {Object.entries(drill.equipment)
            .filter(([, qty]) => qty > 0)
            .map(([name, qty]) => `${qty} ${name}`)
            .join(', ') || 'None'}
        </div>
      )}

      {/* Video Section */}
      {!hasImages && drill.youtubeUrl && (
        <div className="station-video-actions">
          {videoId && (
            <button
              onClick={() => setShowVideo(!showVideo)}
              className="station-video-primary"
            >
              {showVideo ? 'Hide Video' : '\u25B6 Watch Video'}
            </button>
          )}
          <a
            href={drill.youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="station-video-link-subtle"
          >
            Open in YouTube
          </a>
        </div>
      )}

      {/* Image Section */}
      {hasImages && (
        <div className="station-video-actions">
          <button
            onClick={handleShowImages}
            className="station-video-primary"
          >
            {showImages ? 'Hide Images' : 'View Images'}
          </button>
        </div>
      )}

      {/* Embedded Video */}
      {showVideo && videoId && drill.youtubeUrl && (
        <div className="station-video-embed">
          <YouTubeEmbed
            url={drill.youtubeUrl}
            title={`${drill.name} - training video`}
            borderRadius={6}
          />
        </div>
      )}

      {/* Images Gallery */}
      {showImages && drillImages.length > 0 && (
        <div className="mt-2">
          <DrillImageGallery drillId={drill.id} images={drillImages} mode="full" />
        </div>
      )}
    </div>
  );
};

// Time Block Display Component
interface TimeBlockDisplayProps {
  block: TimeBlock;
  coaches: number;
  pitchDimensions: Dimension;
}

const TimeBlockDisplay: React.FC<TimeBlockDisplayProps> = ({ block, coaches, pitchDimensions }) => {
  const getHeaderClass = (blockName: string) => {
    if (blockName === 'Warm-up') return 'time-block-header time-block-header-warmup';
    if (blockName.includes('Main Session')) return 'time-block-header time-block-header-main';
    if (blockName === 'Game' || blockName === 'Small-Sided Games') return 'time-block-header time-block-header-game';
    if (blockName === 'Cool-down') return 'time-block-header time-block-header-cooldown';
    return 'time-block-header';
  };

  const totalStations = block.drillGroups.reduce((sum, g) => sum + g.stations.length, 0);

  return (
    <div className="time-block">
      <div className={getHeaderClass(block.name)}>
        <div className="time-block-name">{block.name}</div>
        <div className="time-block-time">{formatTimeRange(block.startTime, block.duration)}</div>
      </div>

      <div className="time-block-body">
        <PitchLayoutDiagram
          pitchDimensions={pitchDimensions}
          drillGroups={block.drillGroups}
        />

        {block.drillGroups.map((group, idx) => (
          <DrillCard
            key={`${group.drill.id}-${idx}`}
            group={group}
            showCoach={totalStations > 1 && coaches > 1}
          />
        ))}

        {block.rotations && block.rotations.length > 0 && (
          <div className="alert alert-warning mt-3">
            <div className="text-sm font-semibold mb-1">Rotation Schedule:</div>
            {block.rotations.map((rotation, idx) => (
              <div key={idx} className="text-sm">
                At {rotation.atMinute} min: {rotation.description}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Equipment Summary Component
interface EquipmentSummaryDisplayProps {
  equipment: Record<string, number>;
}

const EQUIPMENT_ICONS: Record<string, React.FC<{ size?: number; className?: string }>> = {
  'Cones': Cone,
  'Footballs': Circle,
  'Rugby Balls': Circle,
  'Goals': Goal,
  'Bibs': Shirt,
};

const EquipmentSummaryDisplay: React.FC<EquipmentSummaryDisplayProps> = ({ equipment }) => {
  const items = Object.entries(equipment)
    .filter(([, qty]) => qty > 0)
    .map(([name, qty]) => ({
      label: name,
      value: qty,
      icon: EQUIPMENT_ICONS[name] ?? Package,
    }));

  if (items.length === 0) return null;

  return (
    <div className="equipment-summary">
      <h4 className="equipment-summary-title">Equipment Required</h4>
      <div className="equipment-summary-grid">
        {items.map((item) => {
          const IconComponent = item.icon;
          return (
            <div key={item.label} className="equipment-item">
              <IconComponent size={18} className="equipment-icon" />
              <span className="equipment-label">{item.value} {item.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Session Reactions Component
interface SessionReactionsProps {
  sessionId: number;
  onError?: (message: string) => void;
}

const SessionReactions: React.FC<SessionReactionsProps> = ({ sessionId, onError }) => {
  const [reactions, setReactions] = useState<ReactionSummaryResponse>({ likes: 0, dislikes: 0, userReaction: null });
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    const fetchReactions = async () => {
      const result = await restClient.getSessionReaction(sessionId);
      if (result.IsSuccess) {
        setReactions(result.Value);
      }
    };
    fetchReactions();
  }, [sessionId]);

  const handleToggle = async (isLike: boolean) => {
    const result = await restClient.toggleSessionReaction(sessionId, { isLike });
    if (result.IsSuccess) {
      setReactions(result.Value);
    } else {
      onErrorRef.current?.(result.Error?.description ?? 'Failed to update reaction.');
    }
  };

  return (
    <div className="session-reactions">
      <button
        className={`reaction-btn ${reactions.userReaction === true ? 'reaction-btn-active' : ''}`}
        onClick={() => handleToggle(true)}
        aria-label="Like session"
      >
        <ThumbsUp size={16} />
        <span className="reaction-count">{reactions.likes}</span>
      </button>
      <button
        className={`reaction-btn ${reactions.userReaction === false ? 'reaction-btn-active' : ''}`}
        onClick={() => handleToggle(false)}
        aria-label="Dislike session"
      >
        <ThumbsDown size={16} />
        <span className="reaction-count">{reactions.dislikes}</span>
      </button>
    </div>
  );
};

// Session Comments Component
interface SessionCommentsProps {
  sessionId: number;
  onError?: (message: string) => void;
}

const SessionComments: React.FC<SessionCommentsProps> = ({ sessionId, onError }) => {
  const [comments, setComments] = useState<CommentResponse[]>([]);
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [expandedReplies, setExpandedReplies] = useState<Set<number>>(new Set());
  const maxLength = 2000;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    const fetchComments = async () => {
      const result = await restClient.getComments(sessionId);
      if (result.IsSuccess) {
        setComments(result.Value);
      } else {
        onErrorRef.current?.(result.Error?.description ?? 'Failed to load comments.');
      }
    };
    fetchComments();
  }, [sessionId]);

  const handleAddComment = async () => {
    const trimmed = content.trim();
    if (!trimmed || isSubmitting) return;

    setIsSubmitting(true);
    const result = await restClient.addComment(sessionId, { content: trimmed });
    if (result.IsSuccess) {
      setComments(prev => [result.Value, ...prev]);
      setContent('');
    } else {
      onErrorRef.current?.(result.Error?.description ?? 'Failed to add comment.');
    }
    setIsSubmitting(false);
  };

  const handleAddReply = async (commentId: number) => {
    const trimmed = replyContent.trim();
    if (!trimmed || isSubmitting) return;

    setIsSubmitting(true);
    const result = await restClient.addReply(sessionId, commentId, { content: trimmed });
    if (result.IsSuccess) {
      setComments(prev => prev.map(c => {
        if (c.id === commentId) {
          return { ...c, replies: [...c.replies, result.Value] };
        }
        return c;
      }));
      setReplyContent('');
      setReplyingTo(null);
      setExpandedReplies(prev => new Set(prev).add(commentId));
    } else {
      onErrorRef.current?.(result.Error?.description ?? 'Failed to add reply.');
    }
    setIsSubmitting(false);
  };

  const handleDeleteComment = async (commentId: number, parentId?: number) => {
    if (parentId) {
      setComments(prev => prev.map(c => {
        if (c.id === parentId) {
          return { ...c, replies: c.replies.filter(r => r.id !== commentId) };
        }
        return c;
      }));
    } else {
      setComments(prev => prev.filter(c => c.id !== commentId));
    }

    const result = await restClient.deleteComment(sessionId, commentId);
    if (result.IsFailure) {
      const fetchResult = await restClient.getComments(sessionId);
      if (fetchResult.IsSuccess) {
        setComments(fetchResult.Value);
      }
      onErrorRef.current?.(result.Error?.description ?? 'Failed to delete comment.');
    }
  };

  const handleToggleCommentReaction = async (commentId: number, isLike: boolean, parentId?: number) => {
    const result = await restClient.toggleCommentReaction(sessionId, commentId, { isLike });
    if (result.IsSuccess) {
      const summary = result.Value;
      const updateReaction = (c: CommentReplyResponse): CommentReplyResponse => {
        if (c.id === commentId) {
          return { ...c, likes: summary.likes, dislikes: summary.dislikes, userReaction: summary.userReaction };
        }
        return c;
      };
      setComments(prev => prev.map(c => {
        if (c.id === commentId) {
          return { ...c, likes: summary.likes, dislikes: summary.dislikes, userReaction: summary.userReaction } as CommentResponse;
        }
        if (parentId && c.id === parentId) {
          return { ...c, replies: c.replies.map(updateReaction) };
        }
        return c;
      }));
    } else {
      onErrorRef.current?.(result.Error?.description ?? 'Failed to update reaction.');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, action: () => void) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      action();
    }
  };

  const toggleReplies = (commentId: number) => {
    setExpandedReplies(prev => {
      const next = new Set(prev);
      if (next.has(commentId)) {
        next.delete(commentId);
      } else {
        next.add(commentId);
      }
      return next;
    });
  };

  const formatRelativeTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const renderCommentActions = (comment: CommentReplyResponse, parentId?: number) => (
    <div className="comment-actions">
      <button
        className={`reaction-btn reaction-btn-sm ${comment.userReaction === true ? 'reaction-btn-active' : ''}`}
        onClick={() => handleToggleCommentReaction(comment.id, true, parentId)}
        aria-label="Like comment"
      >
        <ThumbsUp size={14} />
        {comment.likes > 0 && <span className="reaction-count">{comment.likes}</span>}
      </button>
      <button
        className={`reaction-btn reaction-btn-sm ${comment.userReaction === false ? 'reaction-btn-active' : ''}`}
        onClick={() => handleToggleCommentReaction(comment.id, false, parentId)}
        aria-label="Dislike comment"
      >
        <ThumbsDown size={14} />
        {comment.dislikes > 0 && <span className="reaction-count">{comment.dislikes}</span>}
      </button>
      {!parentId && (
        <button
          className="reaction-btn reaction-btn-sm"
          onClick={() => {
            setReplyingTo(replyingTo === comment.id ? null : comment.id);
            setReplyContent('');
          }}
          aria-label="Reply to comment"
        >
          <Reply size={14} />
          <span className="reaction-count">Reply</span>
        </button>
      )}
      {comment.isOwner && (
        <button
          className="reaction-btn reaction-btn-sm reaction-btn-danger"
          onClick={() => handleDeleteComment(comment.id, parentId)}
          aria-label="Delete comment"
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );

  return (
    <div className="session-comments">
      <h4 className="section-title mt-6 mb-4">
        <MessageCircle size={18} className="mr-2" style={{ display: 'inline', verticalAlign: 'text-bottom' }} />
        Comments ({comments.reduce((acc, c) => acc + 1 + c.replies.length, 0)})
      </h4>

      <div className="comment-form">
        <textarea
          className="session-note-textarea"
          placeholder="Add a comment..."
          value={content}
          onChange={e => setContent(e.target.value)}
          onKeyDown={e => handleKeyDown(e, handleAddComment)}
          maxLength={maxLength}
          rows={3}
        />
        <div className="flex justify-between items-center mt-2">
          <span className="text-sm text-muted">
            {content.length}/{maxLength}
          </span>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleAddComment}
            disabled={!content.trim() || isSubmitting}
          >
            {isSubmitting ? 'Posting...' : 'Comment'}
          </button>
        </div>
      </div>

      {comments.length > 0 && (
        <div className="comment-list">
          {comments.map(comment => (
            <div key={comment.id} className="comment-item">
              <div className="comment-header">
                <div className="comment-avatar">
                  {comment.username.charAt(0).toUpperCase()}
                </div>
                <div>
                  <span className="comment-username">{comment.username}</span>
                  <span className="comment-time">{formatRelativeTime(comment.createdAt)}</span>
                </div>
              </div>
              <div className="comment-content">{comment.content}</div>
              {renderCommentActions(comment)}

              {/* Reply form */}
              {replyingTo === comment.id && (
                <div className="comment-reply-form">
                  <textarea
                    className="session-note-textarea"
                    placeholder="Write a reply..."
                    value={replyContent}
                    onChange={e => setReplyContent(e.target.value)}
                    onKeyDown={e => handleKeyDown(e, () => handleAddReply(comment.id))}
                    maxLength={maxLength}
                    rows={2}
                    autoFocus
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => { setReplyingTo(null); setReplyContent(''); }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleAddReply(comment.id)}
                      disabled={!replyContent.trim() || isSubmitting}
                    >
                      Reply
                    </button>
                  </div>
                </div>
              )}

              {/* Replies toggle */}
              {comment.replies.length > 0 && (
                <button
                  className="comment-replies-toggle"
                  onClick={() => toggleReplies(comment.id)}
                >
                  {expandedReplies.has(comment.id)
                    ? `Hide ${comment.replies.length} ${comment.replies.length === 1 ? 'reply' : 'replies'}`
                    : `Show ${comment.replies.length} ${comment.replies.length === 1 ? 'reply' : 'replies'}`}
                </button>
              )}

              {/* Replies */}
              {expandedReplies.has(comment.id) && comment.replies.length > 0 && (
                <div className="comment-replies">
                  {comment.replies.map(reply => (
                    <div key={reply.id} className="comment-reply-item">
                      <div className="comment-header">
                        <div className="comment-avatar comment-avatar-sm">
                          {reply.username.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <span className="comment-username">{reply.username}</span>
                          <span className="comment-time">{formatRelativeTime(reply.createdAt)}</span>
                        </div>
                      </div>
                      <div className="comment-content">{reply.content}</div>
                      {renderCommentActions(reply, comment.id)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Session Overview Component
interface SessionOverviewProps {
  session: TrainingSession;
  isEditable: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onError?: (message: string) => void;
}

const SessionOverview: React.FC<SessionOverviewProps> = ({ session, isEditable, canDelete, onEdit, onDelete, onError }) => {
  return (
    <div className="card mb-4">
      <div className="card-body">
        <div className="flex justify-between items-start mb-3">
          <h3 className="card-title mb-0">{session.name}</h3>
          <div className="flex gap-2">
            {isEditable && (
              <button
                onClick={onEdit}
                className="btn btn-primary btn-sm"
              >
                Edit Session
              </button>
            )}
            {canDelete && (
              <button
                onClick={onDelete}
                className="btn btn-outline-danger btn-sm"
              >
                <Trash2 size={14} /> Delete
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-2 mb-4 flex-wrap items-center">
          <span className="badge badge-lg badge-accent">{session.sessionFocus}</span>
          {session.ageGroup && (
            <span className="badge badge-lg badge-light">{session.ageGroup}</span>
          )}
          <SessionReactions sessionId={session.id} onError={onError} />
        </div>
        <div className="session-overview">
          <div className="stat-card">
            <div className="stat-value stat-value-lg">{session.totalTime}</div>
            <div className="stat-label">minutes</div>
          </div>
          <div className="stat-card">
            <div className="stat-value stat-value-lg">{session.players}</div>
            <div className="stat-label">players</div>
          </div>
          <div className="stat-card">
            <div className="stat-value stat-value-lg">{session.coaches}</div>
            <div className="stat-label">coach{session.coaches !== 1 ? 'es' : ''}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {session.pitchDimensions.width}x{session.pitchDimensions.height}m
            </div>
            <div className="stat-label">pitch size</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Session Notes Component
interface SessionNotesProps {
  sessionId: number;
  onError?: (message: string) => void;
}

const SessionNotes: React.FC<SessionNotesProps> = ({ sessionId, onError }) => {
  const [notes, setNotes] = useState<SessionNoteResponse[]>([]);
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const maxLength = 2000;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    const fetchNotes = async () => {
      const result = await restClient.getSessionNotes(sessionId);
      if (result.IsSuccess) {
        setNotes(result.Value);
      } else {
        onErrorRef.current?.(result.Error?.description ?? 'Failed to load notes.');
      }
    };
    fetchNotes();
  }, [sessionId]);

  const handleAddNote = async () => {
    const trimmed = content.trim();
    if (!trimmed || isSubmitting) return;

    setIsSubmitting(true);
    const result = await restClient.addSessionNote(sessionId, { content: trimmed });
    if (result.IsSuccess) {
      setNotes(prev => [result.Value, ...prev]);
      setContent('');
    } else {
      onError?.(result.Error?.description ?? 'Failed to add note.');
    }
    setIsSubmitting(false);
  };

  const handleDeleteNote = async (noteId: number) => {
    setNotes(prev => prev.filter(n => n.id !== noteId));
    const result = await restClient.deleteSessionNote(sessionId, noteId);
    if (result.IsFailure) {
      // Re-fetch on failure to restore state
      const fetchResult = await restClient.getSessionNotes(sessionId);
      if (fetchResult.IsSuccess) {
        setNotes(fetchResult.Value);
      }
      onError?.(result.Error?.description ?? 'Failed to delete note.');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleAddNote();
    }
  };

  return (
    <div className="session-notes">
      <h4 className="section-title mt-6 mb-4">My Notes</h4>

      <div className="session-note-form">
        <textarea
          className="session-note-textarea"
          placeholder="Add a private note about this session..."
          value={content}
          onChange={e => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={maxLength}
          rows={3}
        />
        <div className="flex justify-between items-center mt-2">
          <span className="text-sm text-muted">
            {content.length}/{maxLength} characters
          </span>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleAddNote}
            disabled={!content.trim() || isSubmitting}
          >
            {isSubmitting ? 'Adding...' : 'Add Note'}
          </button>
        </div>
      </div>

      {notes.length > 0 && (
        <div className="session-notes-list">
          {notes.map(note => (
            <div key={note.id} className="session-note-item">
              <div className="session-note-content">{note.content}</div>
              <div className="session-note-footer">
                <span className="text-sm text-muted">
                  {new Date(note.createdAt).toLocaleString()}
                </span>
                <button
                  className="session-note-delete"
                  onClick={() => handleDeleteNote(note.id)}
                  aria-label="Delete note"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Session Detail Component
interface SessionDetailProps {
  session: TrainingSession;
  isEditable: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onError?: (message: string) => void;
}

const SessionDetail: React.FC<SessionDetailProps> = ({ session, isEditable, canDelete, onEdit, onDelete, onError }) => {
  return (
    <div className="session-detail-panel">
      {/* Session Header/Overview */}
      <SessionOverview session={session} isEditable={isEditable} canDelete={canDelete} onEdit={onEdit} onDelete={onDelete} onError={onError} />

      {/* Equipment Summary */}
      <EquipmentSummaryDisplay equipment={session.equipmentSummary} />

      {/* Time Blocks */}
      <h4 className="section-title mt-6 mb-4">Session Timeline</h4>
      {session.timeBlocks.map(block => (
        <TimeBlockDisplay
          key={block.id}
          block={block}
          coaches={session.coaches}
          pitchDimensions={session.pitchDimensions}
        />
      ))}

      {/* Session Notes (Private) */}
      <SessionNotes sessionId={session.id} onError={onError} />

      {/* Session Comments (Public) */}
      <SessionComments sessionId={session.id} onError={onError} />
    </div>
  );
};

// Empty State Component
const EmptyState: React.FC = () => {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">
        <FolderOpen size={32} />
      </div>
      <p className="empty-state-text">
        No training sessions found. Create a session in the Training Planner to see it here.
      </p>
    </div>
  );
};

// Main Training Sessions Component
export const TrainingSessions: React.FC<TrainingSessionsProps> = ({ onError, isAdmin }) => {
  const navigate = useNavigate();
  const { currentSport } = useReferenceData();
  const [responses, setResponses] = useState<TrainingSessionResponse[]>([]);
  const [filteredResponses, setFilteredResponses] = useState<TrainingSessionResponse[]>([]);
  const [selectedResponse, setSelectedResponse] = useState<TrainingSessionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Use ref to avoid re-fetching when onError callback changes
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Handle filtered sessions change from SessionFilters
  const handleFilteredSessionsChange = useCallback((filtered: TrainingSessionResponse[]) => {
    setFilteredResponses(filtered);

    // Auto-select first visible session if no selection or current selection is filtered out
    setSelectedResponse(prev => {
      if (!prev || !filtered.some(r => r.session.id === prev.session.id)) {
        return filtered.length > 0 ? filtered[0] : null;
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    const fetchSessions = async () => {
      setIsLoading(true);
      const result = await restClient.getTrainingSessions(currentSport);
      if (result.IsSuccess) {
        setResponses(result.Value);
        // Note: Auto-selection is handled by handleFilteredSessionsChange
        // when SessionFilters first applies the default sort
      } else {
        onErrorRef.current?.(result.Error?.description ?? 'Failed to load training sessions.');
      }
      setIsLoading(false);
    };

    fetchSessions();
  }, [currentSport]);

  const handleEditSession = (sessionId: number) => {
    navigate(`/training-planner/${sessionId}`);
  };

  const handleDeleteSession = async () => {
    if (!selectedResponse || isDeleting) return;

    const sessionId = selectedResponse.session.id;
    const previousResponses = responses;

    setIsDeleting(true);
    setShowDeleteConfirmation(false);

    // Optimistic removal
    setResponses(prev => prev.filter(r => r.session.id !== sessionId));

    const result = await restClient.deleteTrainingSession(sessionId);
    if (result.IsFailure) {
      // Restore on failure
      setResponses(previousResponses);
      onError?.(result.Error?.description ?? 'Failed to delete training session.');
    }

    setIsDeleting(false);
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <p className="loading-text">Loading training sessions...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="page-title">Training Sessions</h2>
            <p className="page-description">
              View saved training sessions. Select a session from the list to see its details.
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => navigate('/training-sessions/build-from-pdf')}
              className="btn btn-primary"
            >
              <FileUp size={18} className="mr-2" />
              Build from PDF
            </button>
          )}
        </div>
      </div>

      {responses.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Filters Toolbar */}
          <SessionFilters
            sessions={responses}
            onFilteredSessionsChange={handleFilteredSessionsChange}
          />

          {/* Two-column layout */}
          <div className="training-sessions-layout">
            {/* Left Panel: Session List */}
            <div className="session-list-panel">
              {filteredResponses.length === 0 ? (
                <div className="empty-filter-state">
                  <p className="text-muted text-center p-4">No sessions match your filters</p>
                </div>
              ) : (
                <SessionList
                  responses={filteredResponses}
                  selectedResponse={selectedResponse}
                  onSelect={setSelectedResponse}
                />
              )}
            </div>

            {/* Session Detail (Right Panel) */}
            {selectedResponse && (
              <SessionDetail
                session={selectedResponse.session}
                isEditable={selectedResponse.isEditable}
                canDelete={selectedResponse.isEditable || !!isAdmin}
                onEdit={() => handleEditSession(selectedResponse.session.id)}
                onDelete={() => setShowDeleteConfirmation(true)}
                onError={onError}
              />
            )}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && selectedResponse && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirmation(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Session</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{selectedResponse.session.name}</strong>?</p>
              <p className="text-muted text-sm mt-2">
                This will permanently delete the session along with all its notes, comments, and reactions. This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowDeleteConfirmation(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger btn-sm"
                onClick={handleDeleteSession}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
